import React, { KeyboardEvent, useState } from 'react'

import ApplyToAllHeader from '../BulkTable/ApplyToAllHeader'
import BulkTable, { BulkTableColumn } from '../BulkTable'
import { useBulkTableStyles } from '../BulkTable/styles'
import NumberFieldInput from './NumberFieldInput'
import { NumberField } from './types'

export interface StoreRemunerationValue {
  dsr: number | null
  price: number | null
  salaryBase: number | null
  storeId: string
  storeName: string
  ticket: number | null
  total: number | null
}

interface BulkRemunerationValuesTableProps {
  isTotalReadOnly?: boolean
  values: StoreRemunerationValue[]
  onChange: (storeId: string, field: NumberField, value: number | null) => void
  onApplyToAll: (field: NumberField, value: number) => void
}

const labels: Record<NumberField, string> = {
  salaryBase: 'Piso salarial',
  dsr: 'DSR',
  total: 'Meta mensal',
  ticket: 'Ticket médio',
  price: 'Preço médio'
}

const numberFields: NumberField[] = ['total', 'ticket', 'price', 'salaryBase', 'dsr']
const valueInputSelector = (field: NumberField, rowIndex: number) => `[data-bulk-values-field="${field}"][data-bulk-values-row="${rowIndex}"]`

const requiredMessage: Record<NumberField, string> = {
  salaryBase: 'Informe o piso salarial',
  dsr: 'Informe o DSR',
  total: 'Informe a meta mensal',
  ticket: 'Informe o ticket médio',
  price: 'Informe o preço médio'
}

export const getRemunerationFieldError = (field: NumberField, value: number | null): string | null => {
  const isEmpty = value === null || value === undefined
  if (field === 'dsr') return isEmpty ? requiredMessage[field] : null
  if (isEmpty) return requiredMessage[field]
  if (value <= 0) {
    if (field === 'total') return 'A meta mensal deve ser maior que zero'
    if (field === 'salaryBase') return 'O piso salarial deve ser maior que zero'
    if (field === 'ticket') return 'O ticket médio deve ser maior que zero'
    if (field === 'price') return 'O preço médio deve ser maior que zero'
  }
  return null
}

const BulkRemunerationValuesTable: React.FC<BulkRemunerationValuesTableProps> = ({ isTotalReadOnly = false, values, onChange, onApplyToAll }) => {
  const classes = useBulkTableStyles()
  const inputClassName = `${classes.input} ${classes.compactInput}`
  const [applyValues, setApplyValues] = useState<Record<NumberField, number | null>>({
    salaryBase: null,
    dsr: null,
    total: null,
    ticket: null,
    price: null
  })

  const focusNextRowField = (field: NumberField, rowIndex: number) => {
    const nextInput = document.querySelector<HTMLInputElement>(valueInputSelector(field, rowIndex + 1))
    if (!nextInput) return
    nextInput.focus()
    nextInput.select()
  }

  const handleHeaderKeyDown = (event: KeyboardEvent<HTMLInputElement>, field: NumberField, value: number | null) => {
    if (event.key !== 'Enter' || value === null) return
    event.preventDefault()
    onApplyToAll(field, value)
  }

  const handleCellKeyDown = (event: KeyboardEvent<HTMLInputElement>, field: NumberField, rowIndex: number) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    focusNextRowField(field, rowIndex)
  }

  const buildNumberHeaderControl = (field: NumberField) => {
    const value = applyValues[field]
    const isReadOnly = field === 'total' && isTotalReadOnly
    return (
      <ApplyToAllHeader compact ariaLabel={`Aplicar ${labels[field]} para todas as lojas`} disabled={isReadOnly || value === null} onApply={() => onApplyToAll(field, value as number)}>
        <NumberFieldInput
          className={inputClassName}
          disabled={isReadOnly}
          field={field}
          placeholder='Todos'
          value={value}
          onKeyDown={event => handleHeaderKeyDown(event, field, value)}
          onChange={nextValue => setApplyValues(prev => ({ ...prev, [field]: nextValue }))}
        />
      </ApplyToAllHeader>
    )
  }

  const columns: BulkTableColumn<StoreRemunerationValue>[] = numberFields.map(field => ({
    headerLabel: labels[field],
    headerControl: buildNumberHeaderControl(field),
    renderCell: (row, rowIndex) => {
      const errorMessage = getRemunerationFieldError(field, row[field])
      return (
        <NumberFieldInput
          className={inputClassName}
          disabled={field === 'total' && isTotalReadOnly}
          field={field}
          inputProps={{
            'data-bulk-values-field': field,
            'data-bulk-values-row': rowIndex
          }}
          value={row[field]}
          errorMessage={errorMessage}
          onKeyDown={event => handleCellKeyDown(event, field, rowIndex)}
          onChange={nextValue => onChange(row.storeId, field, nextValue)}
        />
      )
    }
  }))

  return (
    <BulkTable
      compact
      columns={columns}
      rows={values}
      stickyHeader
      tableStyle={{ width: 'auto', maxWidth: 900 }}
    />
  )
}

export default BulkRemunerationValuesTable
